import React from "react";
import { Link } from "react-router-dom";
import { trpc } from "~/lib/trpc";
import { DealChip } from "~/components/ui/DealChip";
import { formatEur, formatKm, type DealRating } from "~/lib/utils";
import { useT } from "~/lib/i18n";

/**
 * Public landing page. Brand register: bigger serif, deliberate spacing,
 * the only "wow" surface in the product. Everything else is product
 * register.
 *
 * Copy lives entirely in `@motorscan/i18n` so the FR and EN versions stay
 * in lockstep. Lists like `steps`, `bands`, `plans`, `qa` carry only
 * structural data (ids, prices, ratings); the text is a dict lookup.
 */
export default function Landing(): React.JSX.Element {
  return (
    <div className="flex flex-col">
      <Hero />
      <HowItWorks />
      <FairPriceProof />
      <Pricing />
      <Faq />
    </div>
  );
}

function Hero(): React.JSX.Element {
  const { t } = useT();
  return (
    <section className="relative border-b border-[var(--divider-weak)] overflow-hidden" style={{ backgroundColor: "oklch(12% 0.02 250)" }}>
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-cover bg-center"
        style={{ backgroundImage: "url(/img/landing/hero.jpg)" }}
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(95deg, oklch(12% 0.02 250 / 0.95) 0%, oklch(12% 0.02 250 / 0.78) 38%, oklch(12% 0.02 250 / 0.20) 72%, transparent 100%)",
        }}
      />

      <div className="relative w-full max-w-[1200px] mx-auto px-5 pt-24 pb-28 md:pt-32 md:pb-36">
        <span className="mono text-[11px] uppercase tracking-[0.14em] text-[oklch(82%_0.02_90)]">
          {t("landing.kicker")}
        </span>
        <h1
          className="mt-4 font-display text-[clamp(40px,7vw,72px)] leading-[1.02] max-w-[18ch]"
          style={{ color: "oklch(98% 0.005 90)" }}
        >
          {t("landing.hero.title")}
        </h1>
        <p
          className="mt-6 text-[17px] leading-[1.55] max-w-[54ch]"
          style={{ color: "oklch(86% 0.01 90)" }}
        >
          {t("landing.hero.subtitle")}
        </p>
        <div className="mt-9 flex items-center gap-3">
          <Link
            to="/scan/new"
            className="inline-flex h-11 items-center gap-2 rounded-[4px] bg-[var(--accent)] px-5 text-[15px] font-medium text-[var(--ink-on-accent)] hover:bg-[var(--accent-hover)] transition-colors duration-[var(--dur-quick)]"
          >
            {t("common.startScanFree")}
          </Link>
          <Link
            to="/marche"
            className="inline-flex h-11 items-center gap-2 rounded-[4px] border px-5 text-[15px] font-medium transition-colors duration-[var(--dur-quick)]"
            style={{
              color: "oklch(96% 0.005 90)",
              borderColor: "oklch(96% 0.005 90 / 0.35)",
              backgroundColor: "oklch(96% 0.005 90 / 0.08)",
            }}
          >
            {t("landing.hero.exploreMarket")}
          </Link>
        </div>
      </div>

      <div className="relative w-full max-w-[1200px] mx-auto px-5 pb-20 -mt-6">
        <LiveDemo />
      </div>
    </section>
  );
}

function LiveDemo(): React.JSX.Element {
  const { t, numberLocale } = useT();
  const q = trpc.fairPrice.topDealsBySlug.useQuery({ brandSlug: "ferrari", modelSlug: "458", limit: 3 });
  const deals = q.data?.deals ?? [];

  return (
    <div className="mt-14 border border-[var(--divider-strong)] rounded-[6px] bg-surface-card overflow-hidden overflow-x-auto">
      <div className="px-4 py-2.5 border-b border-[var(--divider-weak)] flex items-center justify-between bg-surface-sunken">
        <span className="mono text-[11px] uppercase tracking-[0.1em] text-ink-tertiary">
          {t("landing.demo.kicker")}
        </span>
        <span className="text-[11px] text-ink-tertiary">
          {q.isPending ? "…" : t("landing.demo.deals", { n: deals.length })}
        </span>
      </div>
      {/* Deal cards (visual & engaging) */}
      <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {deals.map((d) => {
          const rating = (d.dealRating ?? "unknown") as DealRating;
          return (
            <Link
              key={d.id}
              to={`/listing/${d.id}`}
              className="group flex flex-col rounded-[6px] border border-[var(--divider-weak)] overflow-hidden hover:border-[var(--divider-strong)] transition-all duration-200 hover:shadow-[0_2px_12px_-2px_rgba(0,0,0,0.1)]"
            >
              {/* Thumbnail */}
              <div className="relative w-full aspect-[16/9] bg-surface-sunken overflow-hidden">
                {(d as Record<string, unknown>).thumbnailUrl ? (
                  <img
                    src={(d as Record<string, unknown>).thumbnailUrl as string}
                    alt={d.currentTitle ?? ""}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-ink-tertiary opacity-20">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                      <path d="M5 17h2m10 0h2M3 11l1-5h2l2 3h8l2-3h2l1 5" />
                      <rect x="3" y="11" width="18" height="6" rx="2" />
                    </svg>
                  </div>
                )}
                <div className="absolute top-2 left-2">
                  <DealChip rating={rating} size="sm" />
                </div>
              </div>
              {/* Card body */}
              <div className="p-3 flex-1 flex flex-col gap-1">
                <span className="text-[12.5px] text-ink-primary font-medium line-clamp-1">
                  {d.currentTitle ?? "—"}
                </span>
                <span className="text-[11px] mono text-ink-tertiary">
                  {d.currentYear ?? ""} · {formatKm(d.currentKm, numberLocale)}
                </span>
                <div className="mt-auto pt-1.5 flex items-baseline justify-between">
                  <span className="text-[14px] font-semibold text-ink-primary mono">
                    {formatEur(d.currentPriceEur, { locale: numberLocale })}
                  </span>
                  {d.dealSavingsEur != null && d.dealSavingsEur > 0 && (
                    <span className="text-[12px] font-medium text-deal-super mono">
                      −{formatEur(d.dealSavingsEur, { compact: true, locale: numberLocale })}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
        {q.isPending &&
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex flex-col rounded-[6px] border border-[var(--divider-weak)] overflow-hidden">
              <div className="w-full aspect-[16/9] bg-surface-sunken animate-pulse" />
              <div className="p-3 space-y-2">
                <div className="h-3 w-3/4 bg-surface-hover rounded animate-pulse" />
                <div className="h-2.5 w-1/2 bg-surface-hover rounded animate-pulse" />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function HowItWorks(): React.JSX.Element {
  const { t } = useT();
  const steps = [
    { n: "01", img: "/img/landing/step-search.jpg", h: t("landing.how.step1.h"), p: t("landing.how.step1.p") },
    { n: "02", img: "/img/landing/step-compare.jpg", h: t("landing.how.step2.h"), p: t("landing.how.step2.p") },
    { n: "03", img: "/img/landing/step-alert.jpg", h: t("landing.how.step3.h"), p: t("landing.how.step3.p") },
  ];
  return (
    <section className="border-b border-[var(--divider-weak)]">
      <div className="w-full max-w-[1200px] mx-auto px-5 py-20">
        <span className="mono text-[11px] uppercase tracking-[0.12em] text-ink-tertiary">
          {t("landing.how.kicker")}
        </span>
        <h2 className="mt-2 font-display text-[34px] leading-[42px] text-ink-primary max-w-[20ch]">
          {t("landing.how.title")}
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
          {steps.map((s) => (
            <div key={s.n}>
              <div className="relative aspect-[4/3] overflow-hidden rounded-[4px] bg-surface-sunken">
                <img
                  src={s.img}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[var(--dur-slow)] hover:scale-[1.02]"
                />
                <span
                  className="absolute top-3 left-3 mono text-[10.5px] tracking-[0.14em] px-2 py-1 rounded-[2px]"
                  style={{
                    color: "oklch(98% 0.005 90)",
                    backgroundColor: "oklch(12% 0.02 250 / 0.55)",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  {s.n}
                </span>
              </div>
              <h3 className="mt-4 text-[17px] font-medium text-ink-primary">{s.h}</h3>
              <p className="mt-2 text-[13.5px] leading-[1.55] text-ink-secondary">{s.p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FairPriceProof(): React.JSX.Element {
  const { t } = useT();
  const bands = (
    [
      ["super", t("landing.fairprice.super")],
      ["good", t("landing.fairprice.good")],
      ["fair", t("landing.fairprice.fair")],
      ["high", t("landing.fairprice.high")],
      ["over", t("landing.fairprice.over")],
    ] as const
  );
  return (
    <section className="border-b border-[var(--divider-weak)] bg-surface-sunken">
      <div className="w-full max-w-[1100px] mx-auto px-5 py-16 grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-12">
        <div>
          <span className="mono text-[11px] uppercase tracking-[0.12em] text-ink-tertiary">
            {t("landing.fairprice.kicker")}
          </span>
          <h2 className="mt-2 font-display text-[34px] leading-[42px] text-ink-primary max-w-[18ch]">
            {t("landing.fairprice.title")}
          </h2>
          <p className="mt-5 text-[15px] leading-[1.55] text-ink-secondary max-w-[55ch]">
            {t("landing.fairprice.body1")}
          </p>
          <p className="mt-3 text-[13px] text-ink-tertiary max-w-[55ch]">
            {t("landing.fairprice.body2")}
          </p>
        </div>
        <div>
          <span className="mono text-[11px] uppercase tracking-[0.12em] text-ink-tertiary">
            {t("landing.fairprice.levels")}
          </span>
          <ul className="mt-2 space-y-2.5">
            {bands.map(([r, label]) => (
              <li key={r} className="flex items-center gap-3 text-[13.5px] text-ink-secondary">
                <DealChip rating={r} />
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Pricing(): React.JSX.Element {
  const { t } = useT();
  const week = t("landing.pricing.unitWeek");
  const month = t("landing.pricing.unitMonth");
  const plans = [
    { id: "free", label: "Free", price: "0", unit: week, tagline: t("landing.pricing.free.tagline"), cta: t("landing.pricing.free.cta"), emphasis: false },
    { id: "hunter", label: "Hunter", price: "5", unit: week, tagline: t("landing.pricing.hunter.tagline"), cta: t("landing.pricing.hunter.cta"), emphasis: true },
    { id: "hunter_pro", label: "Hunter Pro", price: "12", unit: week, tagline: t("landing.pricing.hunterPro.tagline"), cta: t("landing.pricing.hunterPro.cta"), emphasis: false },
    { id: "agency", label: "Agency", price: "99", unit: month, tagline: t("landing.pricing.agency.tagline"), cta: t("landing.pricing.agency.cta"), emphasis: false },
  ];
  return (
    <section className="border-b border-[var(--divider-weak)]">
      <div className="w-full max-w-[1100px] mx-auto px-5 py-16">
        <span className="mono text-[11px] uppercase tracking-[0.12em] text-ink-tertiary">
          {t("landing.pricing.kicker")}
        </span>
        <h2 className="mt-2 font-display text-[34px] leading-[42px] text-ink-primary max-w-[20ch]">
          {t("landing.pricing.title")}
        </h2>
        <p className="mt-3 text-[14.5px] text-ink-secondary max-w-[60ch]">
          {t("landing.pricing.intro")}
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {plans.map((p) => (
            <div
              key={p.id}
              className={
                "rounded-[6px] border p-5 flex flex-col " +
                (p.emphasis
                  ? "border-[var(--accent)] bg-[var(--accent-soft)]"
                  : "border-[var(--divider-strong)] bg-surface-card")
              }
            >
              <span className="text-[12px] font-medium uppercase tracking-[0.06em] text-ink-tertiary">
                {p.label}
              </span>
              <p className="mt-2 font-display text-[42px] leading-[44px] text-ink-primary tabular-nums">
                {p.price}
                <span className="text-[13px] text-ink-secondary"> {p.unit}</span>
              </p>
              <p className="mt-3 text-[12.5px] leading-[1.5] text-ink-secondary flex-1">{p.tagline}</p>
              <Link
                to="/scan/new"
                className={
                  "mt-4 inline-flex h-9 items-center justify-center rounded-[4px] text-[13px] font-medium transition-colors duration-[var(--dur-quick)] " +
                  (p.emphasis
                    ? "bg-[var(--accent)] text-[var(--ink-on-accent)] hover:bg-[var(--accent-hover)]"
                    : "border border-[var(--divider-strong)] text-ink-primary hover:bg-surface-hover")
                }
              >
                {p.cta}
              </Link>
            </div>
          ))}
        </div>

        <p className="mt-5 text-[11.5px] text-ink-tertiary">{t("landing.pricing.disclaimer")}</p>
      </div>
    </section>
  );
}

function Faq(): React.JSX.Element {
  const { t } = useT();
  const qa: Array<[string, string]> = [
    [t("landing.faq.q1"), t("landing.faq.a1")],
    [t("landing.faq.q2"), t("landing.faq.a2")],
    [t("landing.faq.q3"), t("landing.faq.a3")],
    [t("landing.faq.q5"), t("landing.faq.a5")],
  ];
  return (
    <section>
      <div className="w-full max-w-[800px] mx-auto px-5 py-16">
        <span className="mono text-[11px] uppercase tracking-[0.12em] text-ink-tertiary">
          {t("landing.faq.kicker")}
        </span>
        <h2 className="mt-2 font-display text-[28px] leading-[36px] text-ink-primary">
          {t("landing.faq.title")}
        </h2>
        <dl className="mt-8 space-y-6">
          {qa.map(([q, a]) => (
            <div key={q} className="border-t border-[var(--divider-weak)] pt-5">
              <dt className="font-medium text-ink-primary text-[15px]">{q}</dt>
              <dd className="mt-1.5 text-[14px] leading-[1.55] text-ink-secondary max-w-[65ch]">{a}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
